/**
 * GettingStarted — first-run checklist shown on the Dashboard until the
 * operator has walked the venue → quiz → game night → hosted chain once.
 * No persistence: each row's done state is computed from live counts, so
 * the card self-dismisses when everything is checked.
 */
import { Link } from 'react-router-dom';
import { Box, Button, Card, Flex, Heading, Text } from '@radix-ui/themes';

interface Step {
  done: boolean;
  title: string;
  detail: string;
  cta?: { label: string; to: string };
  extra?: { label: string; to: string };
}

interface Props {
  venueCount: number;
  quizCount: number;
  eventCount: number;
  hasCompletedEvent: boolean;
}

const GettingStarted: React.FC<Props> = ({
  venueCount,
  quizCount,
  eventCount,
  hasCompletedEvent,
}) => {
  const steps: Step[] = [
    {
      done: venueCount > 0,
      title: 'Add a venue',
      detail: 'The bar or restaurant where you run trivia.',
      cta: { label: 'Venues', to: '/venues' },
    },
    {
      done: quizCount > 0,
      title: 'Create a quiz',
      detail: 'Rounds and questions — write your own or import an existing doc.',
      cta: { label: 'Quizzes', to: '/quizzes' },
      extra: { label: 'or import one', to: '/import' },
    },
    {
      done: eventCount > 0,
      title: 'Schedule a game night',
      detail: 'One night = one quiz at one venue. This generates your audience PIN.',
      cta: { label: 'Game Nights', to: '/live-events' },
    },
    {
      done: hasCompletedEvent,
      title: 'Run it',
      detail:
        'On the night, open Host from this dashboard. Present goes on the venue TV, Score on your phone.',
    },
  ];

  if (steps.every((s) => s.done)) return null;

  return (
    <Card size="3">
      <Heading size="4" mb="1">Getting started</Heading>
      <Text as="p" size="2" color="gray" mb="3">
        Four steps from zero to your first trivia night. This card disappears when
        you&apos;re done.
      </Text>
      <Flex direction="column" gap="3">
        {steps.map((step, i) => (
          <Flex key={step.title} align="center" gap="3">
            <Text
              size="4"
              weight="bold"
              color={step.done ? 'green' : 'gray'}
              style={{ width: 24, textAlign: 'center', flexShrink: 0 }}
              aria-label={step.done ? 'done' : 'not done'}
            >
              {step.done ? '✓' : i + 1}
            </Text>
            <Box flexGrow="1">
              <Text as="div" size="2" weight="medium">
                {step.title}
              </Text>
              <Text as="div" size="1" color="gray">
                {step.detail}
                {step.extra && !step.done && (
                  <>
                    {' '}
                    <Link to={step.extra.to}>{step.extra.label}</Link>
                  </>
                )}
              </Text>
            </Box>
            {step.cta && !step.done && (
              <Button asChild variant="soft" size="1">
                <Link to={step.cta.to}>{step.cta.label}</Link>
              </Button>
            )}
          </Flex>
        ))}
      </Flex>
    </Card>
  );
};

export default GettingStarted;
